import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  Box,
  Flex,
  Label,
  Button,
  Select,
  Spinner,
  Checkbox,
  Accordion,
  Paragraph,
  TextInput
} from '@ubisend/pulse-components';

import FolderTable from './FolderTable';

const StyledOuterScrollableContainer = styled.div`
  position: relative;
  top: 1rem;
  max-height: 24em;
  overflow-y: auto;

  @media (max-width: 1024px) {
    max-height: 20em;
  }
`;

const StyledScrollableContainer = styled.div`
  display: block;
  width: 100%;
  overflow-x: auto;
  outline: none;
  position: relative;
`;

const PreviewSiteTreeWithFilters = ({ data, isLoading }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [combinedFilter, setCombinedFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fileAvailableFilter, setFileAvailableFilter] = useState(false);

  const totalSites = data.length;
  const totalConnected = data.filter(site => site.status?.toLowerCase() === 'connected').length;
  const totalNotConnected = data.filter(site => site.status?.toLowerCase() === 'not connected').length;
  const totalDeleted = data.filter(site => site.status?.toLowerCase() === 'deleted').length;

  const filteredSites = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.filter(site => {
      const siteName = site.site_name?.toLowerCase() || '';
      const combinedMatch =
        siteName.includes(combinedFilter.toLowerCase()) ||
        site.folders?.some(folder =>
          folder.name?.toLowerCase().includes(combinedFilter.toLowerCase())
        );

      const statusMatch = statusFilter
        ? typeof site.status === 'string' &&
        site.status.toLowerCase() === statusFilter.toLowerCase()
        : true;

      const fileMatch = fileAvailableFilter
        ? site.folders?.some(folder => folder.file_count > 0)
        : true;

      return combinedMatch && statusMatch && fileMatch;
    });
  }, [data, combinedFilter, statusFilter, fileAvailableFilter]);

  const sortedSites = useMemo(() => {
    return [...filteredSites].sort((a, b) => {
      const aConnected = a.status?.toLowerCase() === 'connected' ? 1 : 0;
      const bConnected = b.status?.toLowerCase() === 'connected' ? 1 : 0;

      if (aConnected !== bConnected) {
        return bConnected - aConnected;
      }

      const aTotalFiles = Array.isArray(a.folders)
        ? a.folders.reduce((sum, folder) => sum + (folder.file_count || 0), 0)
        : 0;
      const bTotalFiles = Array.isArray(b.folders)
        ? b.folders.reduce((sum, folder) => sum + (folder.file_count || 0), 0)
        : 0;

      if (aTotalFiles !== bTotalFiles) {
        return bTotalFiles - aTotalFiles;
      }

      return a.site_name.localeCompare(b.site_name);
    });
  }, [filteredSites]);

  const statusOptions = [
    { label: 'All', value: '' },
    { label: 'Connected', value: 'connected' },
    { label: 'Not connected', value: 'not connected' },
    { label: 'Deleted', value: 'deleted' }
  ];

  useEffect(() => {
    if (isLoading) {
      setShowFilters(false);
    }
  }, [isLoading]);

  return (
    <Flex col ySpaceLg>
      <Flex>
        <Button
          disabled={isLoading}
          variant="secondary"
          icon={showFilters ? 'eyeOff' : 'eye'}
          onClick={() => setShowFilters(prev => !prev)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </Flex>
      <br />
      {showFilters && (
        <Box border="dashed" mt>
          <Flex col ySpaceSm>
            <Label htmlFor="combinedFilter">Filter by Site or Folder Name</Label>
            <TextInput
              id="combinedFilter"
              placeholder="e.g. Marketing or Documents"
              onChange={e => setCombinedFilter(e.target.value)}
            />

            <Label htmlFor="statusFilter">Filter by Status</Label>
            <Select
              id="statusFilter"
              options={statusOptions}
              onChange={selected => setStatusFilter(selected?.value || '')}
              value={statusOptions.find(option => option.value === statusFilter)}
            />

            <Checkbox
              mt
              label="Only show sites with files"
              checked={fileAvailableFilter}
              onChange={e => setFileAvailableFilter(e.target.checked)}
            />

            <Paragraph sm secondary>
              {
                [
                  totalSites ? `Sites: ${totalSites}` : null,
                  totalConnected ? `Connected: ${totalConnected}` : null,
                  totalNotConnected ? `Not Connected: ${totalNotConnected}` : null,
                  totalDeleted ? `Deleted: ${totalDeleted}` : null,
                ]
                  .filter(Boolean)
                  .join(' | ')
              }
            </Paragraph>
          </Flex>
        </Box>
      )}
      <br />
      <StyledOuterScrollableContainer>
        <StyledScrollableContainer>
          {sortedSites.length === 0 ? (
            <Flex col center middle>
              <Paragraph>No sites match your filters.</Paragraph>
            </Flex>
          ) : (
            sortedSites.map(site => {
              const folders = site.folders;
              const isLoadingFolders = folders === undefined;

              const hasFiles = Array.isArray(folders) &&
                folders.some(folder => folder.file_count > 0);
              const isDisabled = !hasFiles && !isLoadingFolders;

              const totalFolders = Array.isArray(folders) ? folders.length : 0;
              const totalFiles = Array.isArray(folders)
                ? folders.reduce((sum, folder) => sum + (folder.file_count || 0), 0)
                : 0;

              const connectedFiles = folders?.reduce((sum, folder) => {
                return folder.status?.toLowerCase() === 'connected'
                  ? sum + (folder.file_count || 0)
                  : sum;
              }, 0) || 0;

              const notConnectedFiles = folders?.reduce((sum, folder) => {
                return folder.status?.toLowerCase() === 'not connected'
                  ? sum + (folder.file_count || 0)
                  : sum;
              }, 0) || 0;

              const deletedFiles = folders?.reduce((sum, folder) => {
                return folder.status?.toLowerCase() === 'deleted'
                  ? sum + (folder.file_count || 0)
                  : sum;
              }, 0) || 0;

              const content = isLoadingFolders ? (
                <Flex col center middle>
                  <Spinner />
                  <Paragraph sm secondary>Loading folders...</Paragraph>
                </Flex>
              ) : hasFiles ? (
                <>
                  <FolderTable
                    site={site}
                    graphFolder={true}
                    data={folders}
                    orderProps={{}}
                    isLoading={false}
                    has_pending={false}
                  />
                  <Paragraph sm secondary mt>
                    {
                      [
                        totalFolders ? `Folders: ${totalFolders}` : null,
                        totalFiles ? `Files: ${totalFiles}` : null,
                        connectedFiles ? `Connected: ${connectedFiles}` : null,
                        notConnectedFiles ? `Not connected: ${notConnectedFiles}` : null,
                        deletedFiles ? `Deleted: ${deletedFiles}` : null,
                      ]
                        .filter(Boolean)
                        .join(' | ')
                    }
                  </Paragraph>
                </>
              ) : (
                <Paragraph sm secondary>No supported folders found.</Paragraph>
              );

              return (
                <React.Fragment key={site.site_id || site.site_name}>
                  <Accordion
                    header={`${site.site_name} (${site.status})`}
                    open={!isDisabled}
                    disabled={isDisabled}
                  >
                    {content}
                  </Accordion>
                  <br />
                </React.Fragment>
              );
            })
          )}
        </StyledScrollableContainer>
      </StyledOuterScrollableContainer>
    </Flex>
  );
};

PreviewSiteTreeWithFilters.propTypes = {
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool
};

export default PreviewSiteTreeWithFilters;